import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { Firestore } from 'firebase/firestore';
import { Unit } from '../types';

export function useUnitData(db: Firestore, unitId: string | undefined, showError: (message: string) => void) {
  const [unit, setUnit] = useState<Unit | null>(null);
  const [loading, setLoading] = useState(true);
  const [guestList, setGuestList] = useState<any[]>([]);

  useEffect(() => {
    const fetchUnit = async () => {
      if (!unitId) return;

      try {
        const unitDoc = await getDoc(doc(db, 'units', unitId));
        if (unitDoc.exists()) {
          const data = unitDoc.data();
          const unitData: Unit = {
            id: unitDoc.id,
            name: data.name,
            unitNumber: data.unitNumber,
            members: (data.members || []).map((member: any) => {
              // Create member object, only including defined fields (Firestore doesn't allow undefined)
              const cleanedMember: any = {
                uid: member.uid,
                role: member.role,
                joinedAt: member.joinedAt?.toDate() || (member.joinedAt instanceof Date ? member.joinedAt : new Date()),
              };
              
              // Only add optional fields if they're defined (check for undefined/null, not falsy)
              // Empty strings are allowed in Firestore, but undefined is not
              if (member.email !== undefined && member.email !== null) {
                cleanedMember.email = member.email;
              }
              if (member.firstName !== undefined && member.firstName !== null) {
                cleanedMember.firstName = member.firstName;
              }
              if (member.lastName !== undefined && member.lastName !== null) {
                cleanedMember.lastName = member.lastName;
              }
              
              return cleanedMember;
            }),
            createdAt: data.createdAt?.toDate() || new Date(),
            subscription: {
              status: data.subscription?.status || 'trial',
              trialEndsAt: data.subscription?.trialEndsAt?.toDate
                ? data.subscription.trialEndsAt.toDate()
                : data.subscription?.trialEndsAt
                ? new Date(data.subscription.trialEndsAt)
                : undefined,
              billingEmail: data.subscription?.billingEmail || '',
            },
            settings: data.settings || {},
            billingConfig: data.billingConfig,
            creditBalance: data.creditBalance,
            contactEmail: data.contactEmail,
            contactPhone: data.contactPhone,
            address: data.address,
            description: data.description,
            guestList: data.guestList
              ? data.guestList.map((guest: any) => ({
                  ...guest,
                  addedAt: guest.addedAt?.toDate
                    ? guest.addedAt.toDate()
                    : new Date(guest.addedAt),
                }))
              : [],
          };
          setUnit(unitData);
          setGuestList(unitData.guestList || []);
        }
      } catch (error) {
        console.error('Error fetching unit:', error);
        showError('Failed to load unit settings');
      } finally {
        setLoading(false);
      }
    };

    fetchUnit();
  }, [unitId, db, showError]);

  return { unit, setUnit, loading, guestList, setGuestList };
}

